import { Entry } from 'contentful';
import {
  TypeAgeBasedPlayKitLandingPage,
  TypeCollectionPage,
  TypeHomePage,
  TypePage,
  TypePlayKitPage,
  TypePlayKitsLandingPage,
  TypeProductPage,
} from './generated-types';
import { Invoice } from '@/components/checkout/OrderSummary/type';

/**
 * Constants used throughout the application.
 *
 * Note: Object constants should always be created using the `Object.freeze` function.
 *
 * When introducing a new type of constant, please include a comment block with:
 * - a title
 * - description of why it is needed
 * - naming convention
 * - optionally links for more information
 */

type ConstantMapping = {
  [key: string | number]: string;
};

/**
 * Tailwind Class Mappings
 *
 * Naming convention: TW_<title>
 *
 * Tailwind does not support dynamic construction of classnames. In order to reference a tailwind class which
 * is dynamic in nature, you need to identify all the variants which could be generated and have an object mapping
 * each variant directly to the static tailwind class.
 *
 * i.e. colors, spacing, style variations sourced from contentful data.
 *
 * see @link{https://tailwindcss.com/docs/content-configuration#dynamic-class-names} for more information
 */

export const TW_VERTICAL_PADDINGS: ConstantMapping = Object.freeze({
  '0': 'lg:py-0 py-0',
  '4': 'lg:py-4 py-4',
  '6': 'lg:py-6 py-3',
  '8': 'lg:py-8 py-4',
  '10': 'lg:py-10 py-5',
  '12': 'lg:py-12 py-6',
  '14': 'lg:py-14 py-7',
  '16': 'lg:py-16 py-8',
  '20': 'lg:py-20 py-10',
});

export const TW_HORIZONTAL_PADDINGS: ConstantMapping = Object.freeze({
  '0': 'lg:px-0 px-0',
  '4': 'lg:px-4 px-2',
  '6': 'lg:px-6 px-3',
  '8': 'lg:px-8 px-4',
  '10': 'lg:px-10 px-5',
  '12': 'lg:px-12 px-6',
  '14': 'lg:px-14 px-7',
  '16': 'lg:px-16 px-8',
  '20': 'lg:px-20 px-10',
});

const euFooterClasses =
  'grid-areas-euSmall md:grid-areas-euMedium lg:grid-areas-euLarge xl:grid-areas-euXLarge grid-cols-1 md:grid-cols-2 lg:grid-cols-5';

// This works with the grid template areas defined in the Tailwind configuration file.
export const TW_FOOTER_SECTIONS_CLASSES: ConstantMapping = Object.freeze({
  'en-US':
    'grid-areas-usSmall md:grid-areas-usMedium lg:grid-areas-usLarge xl:grid-areas-usXLarge grid-cols-1 md:grid-cols-2 lg:grid-cols-5',
  'en-AU':
    'grid-areas-auSmall md:grid-areas-auMedium lg:grid-areas-auLarge xl:grid-areas-auXLarge grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  es: euFooterClasses,
  de: euFooterClasses,
  nl: euFooterClasses,
  fr: euFooterClasses,
  'en-EU': euFooterClasses,
  'en-GB': euFooterClasses,
});

export const TW_FOOTER_GRID_AREAS: ConstantMapping = Object.freeze({
  learn: 'grid-in-learn',
  products: 'grid-in-products',
  gifting: 'grid-in-gifting',
  podcast: 'grid-in-podcast',
  help: 'grid-in-help',
  app: 'grid-in-app',
  social: 'grid-in-social',
  selectors: 'grid-in-selectors',
  credentials: 'grid-in-credentials',
});

export const TW_BADGE_COLORS = Object.freeze({
  Indigo: {
    bg: 'bg-indigo',
    textColor: 'text-white',
  },
  IndigoDark: {
    bg: 'bg-indigo-extra-dark',
    textColor: 'text-white',
  },
  Aqua: {
    bg: 'bg-aqua',
    textColor: 'text-charcoal',
  },
  Lime: {
    bg: 'bg-lime-green',
    textColor: 'text-charcoal',
  },
  Yellow: {
    bg: 'bg-yellow',
    textColor: 'text-charcoal',
  },
  Orange: {
    bg: 'bg-orange',
    textColor: 'text-charcoal',
  },
  Purple: {
    bg: 'bg-purple',
    textColor: 'text-white',
  },
  Green: {
    bg: 'bg-kelly-green',
    textColor: 'text-white',
  },
  White: {
    bg: 'bg-white',
    textColor: 'text-charcoal',
  },
  Pink: {
    bg: 'bg-pink',
    textColor: 'text-white',
  },
  RedV2: {
    bg: 'bg-red-v2',
    textColor: 'text-white',
  },
  DeepBlueOutline: {
    bg: 'bg-white border-2 border-blue-deep',
    textColor: 'text-blue-deep',
  },
  GreyMidtone: {
    bg: 'bg-grey-midtone',
    textColor: 'text-white',
  },
  BlueDeep: {
    bg: 'bg-blue-deep',
    textColor: 'text-white',
  },
  BlueDeepInverted: {
    bg: 'bg-transparent border-2 border-blue-deep',
    textColor: 'text-blue-deep',
  },
});

export const TW_BORDER_COLORS = Object.freeze({
  aqua: 'border-aqua',
  'aqua-tint': 'border-aqua-tint',
  indigo: 'border-indigo',
  'indigo-tint': 'border-indigo-tint',
  'indigo-pastel': 'border-indigo-pastel',
  'kelly-green': 'border-kelly-green',
  'kelly-green-tint': 'border-kelly-green-tint',
  orange: 'border-orange',
  'orange-tint': 'border-orange-tint',
  pink: 'border-pink',
  'pink-tint': 'border-pink-tint',
  yellow: 'border-yellow',
  'yellow-tint': 'border-yellow-tint',
  tan: 'border-tan',
  'tan-tint': 'border-tan-tint',
  red: 'border-red',
  'red-tint': 'border-red-tint',
  purple: 'border-purple',
  'purple-tint': 'border-purple-tint',
  white: 'border-white',
  'lime-green-tint': 'border-lime-green-tint',
  'lime-green': 'border-lime-green',
  'yellow-pastel': 'border-yellow-pastel',
  'off-white': 'border-off-white',
  'charcoal-v2': 'border-charcoal-v2',
  'red-v2': 'border-red-v2',
});

export const TW_COMPONENT_ROW_VERTICAL_PADDING_CLASSES: ConstantMapping =
  Object.freeze({
    '0': 'lg:py-0 py-0',
    '10': 'lg:py-10 py-5',
    '12': 'lg:py-12 py-6',
    '20': 'lg:py-20 py-10',
    '8-b': 'lg:pb-8 pb:4',
    '10-b': 'lg:pb-10 pb:5',
    '12-b': 'lg:pb-12 pb-6',
    '20-b': 'lg:pb-20 pb-10',
  });

export const TW_COMPONENT_GRID_BOTTOM_PADDING_CLASSES: ConstantMapping =
  Object.freeze({
    '0': 'lg:mb-0 mb-0',
    '10': 'lg:mb-10 mb-5',
    '20': 'lg:mb-20 mb-10',
    '40': 'lg:mb-40 mb-20',
  });

export const TW_DECORATIVE_DIVIDER_MARGIN_TOP_CLASSES: ConstantMapping =
  Object.freeze({
    '0': 'lg:mt-0 mt-0',
    '40': 'mt-5 lg:mt-10',
    '80': 'mt-10 lg:mt-20',
  });

export const TW_DECORATIVE_DIVIDER_MARGIN_BOTTOM_CLASSES: ConstantMapping =
  Object.freeze({
    '0': 'lg:mb-0 mb-0',
    '40': 'mb-5 lg:mb-10',
    '80': 'mb-10 lg:mb-20',
  });

export const TW_PRODUCT_MERCH_CARD_CLASSES: ConstantMapping = Object.freeze({
  container: 'w-[328px] lg:w-[360px]',
  image: 'h-[208px] lg:h-[240px]',
});

export const TW_COURSE_ACCESS_ITEM_CLASSES: ConstantMapping = Object.freeze({
  container: 'max-w-[350px]',
  image: 'h-[208px] lg:h-[240px] object-cover',
});

/**
 * Contentful Content Type Enums
 *
 * Every content model in contentful will have a `contentType` field which is generated
 * when the model is created based off of the model name and remains static as long as
 * that content model exists.
 *
 * This field can be found here: `contentfulObject.sys.contentType.sys.id`
 *  const {
 *     sys: {
 *       contentType: {
 *         sys: { id },
 *       },
 *     },
 *   } = contentfulObject;
 *
 * Naming convention: CONTENT_TYPE_<title>
 *
 * i.e. Page layout templates
 */
export enum CONTENT_TYPE_PRODUCT_SUB_FAQ {
  IMAGE = 'image',
  TEXTBLOCK = 'textBlock',
}

export enum CONTENT_TYPE_MEDIA_TEXT_TITLE {
  LARGE = 'Product Block Large',
  MEDIUM = 'Product Block Medium',
  SMALL = 'Product Block Small',
  VALUE_PROP = 'Value Prop',
}

export enum CONTENT_TYPE_HOMEPAGE_HERO {
  FULL_IMAGE = 'Full Image',
  SIDE_BY_SIDE = 'Side by Side',
  OFFSET = 'Offset',
  PROMO_RICH_TEXT = 'Promo Rich Text',
  PARALLAX = 'Parallax',
}

export enum CONTENT_TYPE_LANDING_PAGE_HERO {
  FULL_IMAGE = 'Full Image',
  FULL_IMAGE_W_WAVE = 'Full Image w/ Wave Border',
  SIDE_BY_SIDE = 'Side by Side',
  CONTAINED = 'Contained Image',
  CONTAINED_W_TRIPTYCH = 'Contained Image w/ Triptych',
}

export enum CONTENT_TYPE_PRODUCT_PAGE {
  PRODUCT_PAGE = 'productPage',
  PRODUCT_LANDING_PAGE = 'productLandingPage',
  PLAY_KIT_PAGE = 'playKitPage',
  PLAY_KITS_LANDING_PAGE = 'playKitsLandingPage',
  AGE_BASED_PLAY_KITS_LANDING_PAGE = 'ageBasedPlayKitLandingPage',
}

export enum CONTENT_TYPE_PRODUCT {
  PRODUCT = 'product',
}

export enum CONTENT_TYPE_PAGE {
  PAGE = 'page',
}

export enum CONTENT_GROUP_REFERRAL_PAGE {
  LANDING_PAGE = 'referralLandingPage',
}

export enum CONTENT_TYPE_COLLECTION_PAGE {
  COLLECTION_PAGE = 'collectionPage',
}

export enum SEO_PAGES {
  COLLECTION_PAGE = 'collectionPage',
  PAGE = 'page',
  PLAY_KITS_LANDING_PAGE = 'playKitsLandingPage',
  PLAY_KIT_PAGE = 'playKitPage',
  PRODUCT_LANDING_PAGE = 'productLandingPage',
  PRODUCT_PAGE = 'productPage',
  AGE_BASED_PLAY_KITS_LANDING_PAGE = 'ageBasedPlayKitLandingPage',
}

export enum CONTENT_TYPE_ACCOUNT_PAGE {
  ACCOUNT_PAGE = 'accountPage',
}

export enum CONTENT_TYPE_LINK {
  INTERNAL = 'link_internal',
  EXTERNAL = 'link_external',
  ADD_TO_CART = 'addToCartCta',
  CTA_WITH_TRAILING_TEXT = 'ctaWithTrailingText',
}

export const CONTENT_TYPE_COMPONENT_ROW = 'componentRow';
export const CONTENT_TYPE_COURSE_ACCESS_ITEM = 'courseAccessItem';
export const CONTENT_TYPE_MEDIA_COLLECTION_ACCESS_ITEM =
  'mediaCollectionAccessItem';
export const CONTENT_TYPE_PRODUCT_MERCH_CARD_EXPLORER =
  'productMerchCardExplorer';
export const CONTENT_TYPE_DECORATIVE_DIVIDER = 'decorativeDivider';

export type ContentfulPage =
  | TypeHomePage
  | TypeCollectionPage
  | TypePage
  | TypePlayKitPage
  | TypePlayKitsLandingPage
  | TypeProductPage
  | TypeAgeBasedPlayKitLandingPage;

export enum CONTENT_TYPE_PRODUCT_MERCH_CARD_LAYOUT_TYPE {
  DEFAULT = 'Default',
  CART_CTA = 'CartCta',
}
/**
 * Contentful Content Group Enums
 *
 * In some cases, the `contentType` field is not specific enough for our needs. For example,
 * when we have one content model driving several layouts. A solution for this is adding a field called `contentGroup`
 * to the model in question. This contentGroup can then be used to map contentful entries to different style variants
 * or page layouts. Note: this field is not part of the contentful entry by default, it has to be manually added to
 * content models.
 *
 * Naming convention: CONTENT_GROUP_<title>
 */

export enum CONTENT_GROUP_ACCOUNT_PAGE {
  MY_COURSE_PACKS = 'myCoursePacks',
  COURSE_STREAMING_PAGE = 'courseStreamingPage',
  MY_ACCOUNT_PAGE = 'myAccountPage',
  ACCOUNT_SETTINGS_PAGE = 'accountSettingsPage',
  ADD_CHILD_PAGE = 'addChildPage',
  EDIT_CHILD_PAGE = 'editChildPage',
  SUBSCRIBER_SHOP_PAGE = 'subscriberShopPage',
  SUBSCRIBER_SHOP_FAMILY_PAGE = 'subscriberShopFamilyPage',
  SUBSCRIBER_SHOP_WELCOME_PAGE = 'subscriberShopWelcomePage',
  PAYMENT_SCHEDULE_PAGE = 'paymentSchedulePage',
  PAYMENT_METHOD_PAGE = 'paymentMethodPage',
  REPLACEMENT_PARTS_PRODUCTS_PAGE = 'replacementPartsProductsPage',
  REPLACEMENT_PARTS_COMPONENTS_PAGE = 'replacementPartsComponentsPage',
  REPLACEMENT_PARTS_SUBCOMPONENTS_PAGE = 'replacementPartsSubcomponentsPage',
  READING_SKILL_SET = 'readingSkillSet',
  READING_SKILL_SET_STREAMING_PAGE = 'readingSkillSetStreamingPage',
  EDIT_COMMUNICATION_PREFERENCES_PAGE = 'editCommunicationPreferencesPage',
  PLAY_KIT_SUBSCRIPTIONS_PAGE = 'playKitSubscriptionsPage',
  ADD_MORE_SAVE_MORE_PAGE = 'addMoreSaveMorePage',
  EARLY_READER_CLUB_SUBS_PAGE = 'earlyReaderClubSubsPage',
  ERC_REACTIVATE_PAGE = 'earlyReaderClubReactivatePage',
  ERC_REACTIVATE_SUCCESS_PAGE = 'earlyReaderClubReactivateSuccessPage',
  ERC_MANAGE_PAYMENT = 'ercManagePayment',
  ERC_MODIFY_PAGE = 'ercModifyPage',
  ERC_MODIFY_EDIT_DATE_PAGE = 'ercModifyEditDatePage',
  ERC_MODIFY_EDIT_DATE_SUCCESS_PAGE = 'ercModifyEditDateSuccessPage',
  ERC_PAUSE_SUCCESS_PAGE = 'ercPauseSuccessPage',
  PK_MODIFY_PAGE = 'pkModifyPage',
  PK_MODIFY_EDIT_DATE_PAGE = 'pkModifyEditDatePage',
  PK_MODIFY_EDIT_DATE_SUCCESS_PAGE = 'pkModifyEditDateSuccessPage',
  PK_SWITCH_SUBSCRIPTION_PAGE = 'pkSwitchSubscriptionPage',
  PK_SWITCH_SUBSCRIPTION_SUCCESS_PAGE = 'pkSwitchSubscriptionSuccessPage',
}

export type TypeAccountPageContentGroup = CONTENT_GROUP_ACCOUNT_PAGE;

/**
 * Display Component Mappings
 *
 * In some cases, a single contentful content model can be used in several different components.
 * This could be due to style variations being significant enough it justifies having separate components.
 *
 * When a contentful model has a radio select option which controls which variant to display
 * this constant type can be used to map those selections to the correct component in our codebase.
 *
 * Naming convention: COMPONENT_MAPPING_<title>
 *
 * i.e. FooterSections
 * see @link{https://app.contentful.com/spaces/0sea1vycfyqy/environments/master/entries/4ZDumJUpVHETWzy2xHEZwV}
 */
export const COMPONENT_MAPPING_FOOTER_SECTIONS = Object.freeze({
  app: 'app',
  social: 'social',
  selectors: 'selectors',
  credentials: 'credentials',
});

/**
 * Shopify CDN Assets
 *
 * We should probably refrain from using asssets hosted on shopify cdn and instead pull these from
 * contentful, but we do have a few usages we need to support. For any new additions here, consider sourcing
 * from contentful or a local project file.
 *
 * Naming convention: SHOPIFY_CDN_ASSET_<title>
 *
 */
export const SHOPIFY_CDN_ASSET_LOADING_GIF = `https://cdn.shopify.com/s/files/1/2386/2119/files/bg-clear-loader.gif?v=1652807126`;
export const SHOPIFY_CDN_ASSET_SLIDER_ARROW =
  'https://cdn.shopify.com/s/files/1/0411/9497/9489/files/ic-right-carousel-desktop_32.svg?v=1678926104';

/**
 * Contentful Error Kind Enum
 *
 * The contentful Error Directive and Error Message models support giving content authors the ability
 * to customize error messaging across the site. In some cases, we may need to handle multiple kinds
 * of errors on a single page. This means we need a way to map an error directive from contentful
 * to our code which handles these errors so we can display relevant error messages to the user.
 */
export enum ERROR_KINDS {
  DEFAULT = 'default',
  CUSTOM = 'custom',
  COURSE_PLAYER_ASSET_STREAMING_FAILURE = 'course player asset streaming failure',
  COURSE_PLAYER_ASSET_UPDATE_FAILURE = 'course player asset update failure',
  COURSE_PLAYER_UNAVAILABLE = 'course player unavailable',
  CORE_DATA_DEPENDENCY_FAILURE = 'core data dependency failure',
  FLOW_BLOCKING_API_FAILURE = 'flow blocking api failure',
}

export type TypeErrorKind = (typeof ERROR_KINDS)[keyof typeof ERROR_KINDS];

/**
 * Misc.
 *
 * put your constant here if it doesn't belong in any of the constant categories
 * described and is unlikely to have similar constant types introduced.
 *
 * When introducing a new miscellaneous constant, identify if there are any
 * others that make sense to refactor and group together as a new constant type.
 *
 */
export const BREAKPOINTS = Object.freeze({
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1440,
});

export enum THEME_COLORS {
  primary = '#536DC4',
  white = '#FFF',
}

export const DESKTOP_MAX_ITEMS = 4;

export const CAROUSEL_SLIDES_TO_SHOW = 5;

export enum SHOPIFY_DOMAIN {
  AU = 'lovevery-au-prod.myshopify.com',
  EU = 'lovevery-international-prod.myshopify.com',
  UK = 'lovevery-uk-prod.myshopify.com',
  US = 'lovevery.myshopify.com',
  AU_STAGING = 'lovevery-au-staging.myshopify.com',
  EU_STAGING = 'lovevery-international-staging.myshopify.com',
  UK_STAGING = 'lovevery-uk-staging.myshopify.com',
  US_STAGING = 'lovevery-kits.myshopify.com',
}

// Checks if the current environment is a staging environment
export function isStageEnvironment(): boolean {
  const currentDomain = getShopifyDomain();

  return (
    currentDomain === SHOPIFY_DOMAIN.US_STAGING ||
    currentDomain === SHOPIFY_DOMAIN.UK_STAGING ||
    currentDomain === SHOPIFY_DOMAIN.AU_STAGING ||
    currentDomain === SHOPIFY_DOMAIN.EU_STAGING
  );
}

export function getShopifyDomain() {
  return process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN as SHOPIFY_DOMAIN;
}

export const YOTPO_PLAYKIT_PROGRAM_PRODUCT_IDS = Object.freeze({
  [SHOPIFY_DOMAIN.US]: '1340940681295',
  [SHOPIFY_DOMAIN.US_STAGING]: '1340940681295',
  [SHOPIFY_DOMAIN.AU]: '7208906752140',
  [SHOPIFY_DOMAIN.AU_STAGING]: '7208906752140',
  [SHOPIFY_DOMAIN.EU]: null,
  [SHOPIFY_DOMAIN.EU_STAGING]: null,
  [SHOPIFY_DOMAIN.UK]: null,
  [SHOPIFY_DOMAIN.UK_STAGING]: null,
});

export function getContentType(contentfulEntry: Entry<any> | undefined) {
  const { id } = contentfulEntry?.sys?.contentType?.sys || {};

  return id || '';
}

export function getContentGroup(linkContent: Entry<any>): string {
  return linkContent?.fields?.contentGroup || '';
}

export function getEntryLocale(contentfulEntry?: Entry<any>): string {
  return contentfulEntry?.sys?.locale || '';
}

export const defaultLocale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'en-US';

// TLDs can each support multiple locales, so order matters (specific to least specific per TLD)
export const domainToLocaleMapping = {
  '.eu/de': 'de',
  '.eu/es': 'es',
  '.eu/fr': 'fr',
  '.eu/nl': 'nl',
  '.eu': 'en-EU',
  '.co.uk': 'en-GB',
  'com.au': 'en-AU',
  '.com': 'en-US',
};
export const locales =
  process.env.LOCALES?.split(',') || Object.values(domainToLocaleMapping);

export const TOOLTIP_STYLING = Object.freeze({
  BACKGROUND_COLOR: 'white',
  BOX_SHADOW:
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  TEXT_COLOR: 'rgb(81 79 78 / var(--tw-text-opacity))',
  WIDTH: '12.5rem',
});

export const COURSE_URLS = [
  'the-course-packs',
  'the-potty-learning-course-pack',
  'the-new-sibling-course-pack',
  'the-food-before-1-course-pack',
  'the-tummy-time-course-pack',
];

export enum CONTENT_TYPE_REPLACEMENT_PARTS {
  PRODUCTS = 'Products',
  COMPONENTS = 'Components',
  SUBCOMPONENTS = 'Subcomponents',
}
/**
 * Subscription Flow constants
 *
 * Mostly used for tracking analytics
 *
 */
export const SUBFLOW_PATHS = {
  CHILD_INFO: 'child-info',
  STARTING_KIT: 'starting-kit',
  PAYMENT_OPTIONS: 'payment-options',
  ADD_ONS: 'add-ons',
  ORDER_REVIEW: 'order-review',
};

export type SUBFLOW_STEP_TYPE = {
  path: string;
  name: string;
  stepNum: number;
};
export const SUBFLOW_STEPS: SUBFLOW_STEP_TYPE[] = [
  {
    path: SUBFLOW_PATHS.CHILD_INFO,
    name: 'Child Info',
    stepNum: 1,
  },
  {
    path: SUBFLOW_PATHS.STARTING_KIT,
    name: 'Starting Kit',
    stepNum: 2,
  },
  {
    path: SUBFLOW_PATHS.PAYMENT_OPTIONS,
    name: 'Payment Options',
    stepNum: 3,
  },
  {
    path: SUBFLOW_PATHS.ADD_ONS,
    name: 'Add-ons',
    stepNum: 4,
  },
  {
    path: SUBFLOW_PATHS.ORDER_REVIEW,
    name: 'Order Review',
    stepNum: 5,
  },
];

export const SUBFLOW_STEPS_V3: SUBFLOW_STEP_TYPE[] = [
  {
    path: SUBFLOW_PATHS.CHILD_INFO,
    name: 'Child Info',
    stepNum: 1,
  },
  {
    path: SUBFLOW_PATHS.STARTING_KIT,
    name: 'Starting Kit',
    stepNum: 2,
  },
  {
    path: SUBFLOW_PATHS.PAYMENT_OPTIONS,
    name: 'Payment Options',
    stepNum: 2,
  },
  {
    path: SUBFLOW_PATHS.ORDER_REVIEW,
    name: 'Order Review',
    stepNum: 3,
  },
];

export enum SUBSCRIPTION_EVENT_LABELS {
  SUBSCRIPTION_FLOW_STARTED = 'Subscription Flow Started',
  SUBSCRIPTION_STEP_VIEWED = 'Subscription Step Viewed',
  SUBSCRIPTION_STEP_COMPLETED = 'Subscription Step Completed',
  SUBSCRIPTION_FLOW_COMPLETED = 'Subscription Flow Completed',
  ITEM_CLICKED = 'Item Clicked',
  ERROR_VIEWED = 'Error Viewed',
  RECOMMENDED_PLAY_KIT_MODAL_VIEWED = 'Recommended Play Kit Modal Viewed V3',
  RECOMMENDED_PLAY_KIT_MODAL_DISMISSED = 'Recommended Play Kit Modal Dismissed V3',
  SELECTED_CUSTOM_OPTION = 'Selected Custom Option Doll Color',
}

export enum CLICKED_ITEM_TYPES {
  LINK = 'Link',
}

export enum ERROR_TYPES {
  FORM_VALIDATION = 'Form Validation',
  SUBFLOW_V3_CHECKOUT_ERROR = 'SUBFLOW_V3_CHECKOUT_ERROR',
}

interface ExcludeFromSitemapInt {
  [key: string]: string;
}

export const EXCLUDE_FROM_SITEMAP: ExcludeFromSitemapInt = {
  StreamlinedSubflow: '[Streamlined Subflow]',
};

export enum SUBFLOW_VERSION {
  V3 = 'v3',
}

const PROCESSING_BUTTONS = ['', 'confirm', 'cancel'] as const;

export type ProcessingButtonType = (typeof PROCESSING_BUTTONS)[number];

export enum ITEM_ORIGINS {
  SUB_FLOW = 'sub_flow',
  PRODUCT_PAGE = 'product_page',
  CUSTOMER_PORTAL = 'customer_portal',
  SUB_SHOP = 'sub_shop',
  REPLACEMENT_PART_SHOP = 'replacement_part_shop',
  ADD_MORE_SAVE_MORE = 'add_more_save_more',
  PREPAY_AND_SAVE = 'prepay_and_save',
  GIFTING = 'gifting',
}

export enum CartItemProperty {
  ItemOrigin = 'item_origin',
  IsSkillSet = 'is_skill_set',
  IsCoursePack = 'is_course_pack',
  PlayKitSubscriptionSettings = 'subscriptionsPayload',
  PlayKitGiftSubscriptionSettings = 'gifts',
  SubscriptionPlanSettings = 'subscription_plan_settings',
  IsPlayKitSubscriptionBookBundleAddon = 'is_book_bundle',
  LinkedProductHandle = 'linked_product_handle',
  QuantityLimit = 'quantity_limit',
  QuantityLimitedToOne = 'quantity_limited_to_one',
  Source = 'source',
  PriceSource = 'price_source',
}

export enum REFERRAL_EVENT_LABELS {
  REFERRAL_GENERATED = 'Referral Generated',
  REFERRAL_LINK_COPIED = 'Referral Link Copied',
}

export enum CONTENT_TYPE_REFERRAL_PAGE {
  REFERRAL_PAGE = 'referralPage',
}

type Email = string;
type PhoneNumber = string;

enum PaymentMethod {
  CreditCard = 'CreditCard',
  PayPal = 'PayPal',
  BankTransfer = 'BankTransfer',
}

export interface BaseAddress {
  first_name: string;
  last_name: string;
  company: string;
  line1: string;
  line2: string;
  city: string;
  country: string;
  state: string;
  zip_code: string;
  phone_number?: PhoneNumber;
  country_code: string;
  email: Email;
}

type ShippingAddress = BaseAddress;
type Address = BaseAddress;

export interface UserAccount extends BaseAddress {
  accepts_sms_marketing?: boolean;
  lovevery_id?: any;
}

interface Customer {
  email: Email;
  accepts_sms_marketing: boolean;
  lovevery_id: string;
}

export interface Purchase {
  customer: Customer;
  shipping_address: ShippingAddress;
  billing_address?: Address;
}

export interface IntermediateStepPayload {
  source: string;
  invoice_id: string;
  purchase: Purchase;
}

export interface WalletIntermediatePayload {
  source: string;
  invoice_id: string;
  purchase: {
    shipping_address: Partial<ShippingAddress>;
  };
}

export interface RedeemCodePayload {
  source: string;
  invoice_uuid: string;
  code: string;
}

export interface RedeemCodeResponse {
  invoice: Invoice | null;
  is_discount: string;
}

export interface RemoveCodePayload {
  source: string;
  invoice_uuid: string;
  remove_discount: boolean;
  remove_gift_card: boolean;
}

interface SetupIntent {
  id: string;
  object: string;
  automatic_payment_methods: null;
  cancellation_reason: null | string;
  client_secret: string;
  created: number;
  description: null | string;
  last_setup_error: null | string;
  livemode: boolean;
  next_action: null | any; // Assuming there is another interface or type for action details
  payment_method: PaymentMethod;
  payment_method_configuration_details: null | any; // Specific configuration details if needed
  payment_method_types: string[];
  status: string;
  usage: string;
}

interface Memo {
  payment_method_type: string;
  preferred_language: string;
}

interface FinalStepCustomer {
  email: Email;
  preferred_language: string;
  accepts_marketing: boolean;
  accepts_sms_marketing: boolean;
}

interface FinalStePurchase {
  payment_processor: string;
  setup_intent: SetupIntent;
  stripe_customer: string;
  memo: Memo;
  invoice_uuid: string;
  customer: FinalStepCustomer;
  shipping_address: Address;
  billing_address: Address;
}

export interface CompleteCheckoutPayload {
  source: string;
  purchase: FinalStePurchase;
  invoice_id: string;
  has_opted_in_to_recurring_puf: boolean;
  language: string;
}

export interface SelectShippingOptionPayload {
  source: string;
  invoice_id: string;
  selected_shipping_option_uuid: string | null;
  memo: {
    preferred_language: string;
  };
}

export const CUSTOMER_PORTAL_SOURCES = [
  'subscription_extension',
  'prepay_and_save',
];

export const ERC_SUBSCRIPTION_PLAN_ANCHOR = 'the-early-reader-club';

export const RSS_MIN_AGE_START_VALUE = 49;
